import { useEffect, useState } from "react";
import clsx from "clsx";
import WithdrawModal from "@app/components/withdraw-modal";
import RefCodeService from "@app/services/http/ref.code.service";
import { openPortalDialog } from "@app/services/modal.service";
import { RefCode } from "@app/types";
import { formatMoneyVN } from "@app/utils/util";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import useObservable from "@core/hooks/use-observable.hook";
import { useT } from "@app/contexts/preferences.context";
import { addToast } from "@app/components/toast/toast.service";
import {
  IcBanknote,
  IcCopy,
  IcExternalLink,
  IcGift,
  IcLink,
  IcReceipt,
  IcShare,
  IcUserPlus,
  IcUsers,
  IcWallet,
} from "@app/components/icons";
import { LoadingBlock, StatusPill, btn, btnGhost, btnPrimary, btnSm } from "@app/components/ui";
import { CommissionList, ReferredList, WithdrawList } from "./ref-lists";

const BASE_REGISTER_URL = "https://pcrender.com/register";
const POLICY_URL = "https://pcrender.vn/chinh-sach-hoa-hong";
/** Số tiền rút tối thiểu, giống createWithdrawValidaion */
const MIN_WITHDRAW = 500000;

type Tab = "commission" | "referred" | "withdraw";

/** Trang Giới thiệu bạn bè: dữ liệu và API giữ nguyên như RefCodePage cũ, giao diện theo bản thiết kế chung. */
function RefCodePage() {
  const t = useT();
  const [refCodes, setRefCodes] = useState<RefCode[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState<Tab>("commission");
  const [counts, setCounts] = useState<Partial<Record<Tab, number>>>({});
  const [withdrawReload, setWithdrawReload] = useState(0);

  const [update, forceUpdate] = useForceUpdate();
  const { subscribeUntilDestroy } = useObservable();

  useEffect(() => {
    subscribeUntilDestroy(RefCodeService.getAllRefCodes(), (res) => {
      setRefCodes(res || []);
      setLoaded(true);
    });
  }, [update]);

  const setCount = (key: Tab) => (n: number) => setCounts((c) => (c[key] === n ? c : { ...c, [key]: n }));

  const copy = (text: string, message: string) => {
    navigator.clipboard?.writeText(text).then(() => addToast({ text: t(message) }));
  };

  const handleWithdraw = (refCode: RefCode) => {
    openPortalDialog(WithdrawModal, { refCode })
      .afterClosed()
      .subscribe((res) => {
        if (res?.ok) {
          addToast({ text: t("Đã gửi yêu cầu rút tiền") });
          forceUpdate();
          setWithdrawReload((n) => n + 1);
          setTab("withdraw");
        }
      });
  };

  const refCode = refCodes[0];
  const maxPercent = Math.max(0, ...refCodes.map((c) => c.commission_percentage || 0));

  const header = (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-[26px] font-bold tracking-[-.015em] max-[760px]:text-[22px]">{t("Giới thiệu bạn bè")}</h1>
        <p className="m-0 mt-1 max-w-[64ch] text-muted">
          {t("Mời bạn bè dùng PCRender và nhận hoa hồng trên mỗi đơn thuê máy, Cloud và VPS của họ.")}
          {maxPercent > 0 && (
            <>
              {" "}
              {t("Hoa hồng lên đến")} <b className="text-fg">{maxPercent}%</b>.
            </>
          )}
        </p>
      </div>
      <a className={clsx(btn, btnGhost)} href={POLICY_URL} target="_blank" rel="noreferrer">
        <IcExternalLink className="h-[18px] w-[18px]" />
        {t("Chính sách hoa hồng")}
      </a>
    </div>
  );

  if (!loaded) {
    return (
      <div className="flex flex-col gap-6 px-6 pb-12 pt-6 max-[900px]:px-4">
        {header}
        <LoadingBlock className="rounded-[14px] border border-line bg-surface" />
      </div>
    );
  }

  if (!refCode) {
    return (
      <div className="flex flex-col gap-6 px-6 pb-12 pt-6 max-[900px]:px-4">
        {header}
        <div className="flex flex-col items-center gap-1.5 rounded-[14px] border border-dashed border-line-2 bg-surface px-4 py-14 text-center">
          <div className="mb-2 grid h-14 w-14 place-items-center rounded-2xl bg-brand-soft text-brand">
            <IcGift className="h-[26px] w-[26px]" />
          </div>
          <h3 className="text-base font-bold">{t("Bạn chưa có mã giới thiệu")}</h3>
          <p className="m-0 max-w-[48ch] text-muted">
            {t("Liên hệ PCRender để được cấp mã giới thiệu và bắt đầu nhận hoa hồng.")}
          </p>
        </div>
      </div>
    );
  }

  const referralLink = `${BASE_REGISTER_URL}?ref_code=${refCode.code}`;
  const canWithdraw = refCode.isActive && refCode.commission_balance >= MIN_WITHDRAW;

  const tabs: [Tab, string, typeof IcReceipt][] = [
    ["commission", "Lịch sử hoa hồng", IcReceipt],
    ["referred", "Người được giới thiệu", IcUsers],
    ["withdraw", "Yêu cầu rút tiền", IcBanknote],
  ];

  return (
    <div className="flex flex-col gap-6 px-6 pb-12 pt-6 max-[1100px]:pb-24 max-[900px]:px-4 max-[760px]:gap-[18px]">
      {header}

      <ol className="m-0 grid list-none grid-cols-3 gap-2.5 p-0 max-[760px]:grid-cols-1 max-[760px]:gap-1.5">
        {[
          ["Chia sẻ link hoặc mã", "Gửi link giới thiệu, hoặc nhờ bạn bè nhập mã khi đăng ký."],
          ["Bạn bè thuê dịch vụ", `Mỗi đơn thuê máy, Cloud, VPS trong ${refCode.period} ngày đầu đều được tính.`],
          ["Nhận hoa hồng, rút về ngân hàng", `Hoa hồng ${refCode.commission_percentage}% vào ví, rút khi đủ ${formatMoneyVN(MIN_WITHDRAW)}đ.`],
        ].map(([title, desc], i) => (
          <li
            key={title}
            className="flex items-start gap-2.5 rounded-card border border-line bg-surface px-3.5 py-2.5 text-[12.5px] leading-snug text-muted max-[760px]:items-center max-[760px]:px-3 max-[760px]:py-2"
          >
            <span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-brand-soft text-xs font-bold text-brand">
              {i + 1}
            </span>
            <span>
              <b className="block text-[13px] font-semibold text-fg">{t(title)}</b>
              <span className="max-[760px]:hidden">{t(desc)}</span>
            </span>
          </li>
        ))}
      </ol>

      <div className="grid grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)] gap-5 max-[1000px]:grid-cols-1">
        {/* Mã + link giới thiệu */}
        <section className="flex flex-col gap-4 rounded-[14px] border border-line bg-surface p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-brand-soft text-brand">
                <IcShare className="h-5 w-5" />
              </span>
              <div>
                <span className="block text-[12.5px] font-semibold text-muted">{t("Mã giới thiệu của bạn")}</span>
                <b className="font-mono text-2xl font-bold tracking-wide text-brand">{refCode.code}</b>
              </div>
            </div>
            <StatusPill tone={refCode.isActive ? "ok" : "bad"}>{refCode.isActive ? t("Đang chạy") : t("Tạm dừng")}</StatusPill>
          </div>

          <div className="flex flex-col gap-1.5">
            <span className="text-[12.5px] font-semibold text-muted">{t("Link giới thiệu")}</span>
            <div className="flex gap-2 max-[560px]:flex-col">
              <div className="flex h-10 min-w-0 flex-1 items-center gap-2 rounded-[10px] border border-line bg-surface-2 px-3">
                <IcLink className="h-4 w-4 flex-none text-faint" />
                <span className="truncate font-mono text-[12.5px]" title={referralLink}>
                  {referralLink}
                </span>
              </div>
              <button type="button" className={clsx(btn, btnPrimary, "!h-10")} onClick={() => copy(referralLink, "Đã sao chép link giới thiệu")}>
                <IcCopy className="h-4 w-4" />
                {t("Sao chép link")}
              </button>
            </div>
            <button
              type="button"
              className={clsx(btn, btnGhost, btnSm, "w-fit")}
              onClick={() => copy(refCode.code, "Đã sao chép mã giới thiệu")}
            >
              <IcCopy className="h-4 w-4" />
              {t("Sao chép mã")}
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2 max-[560px]:grid-cols-1">
            {(
              [
                ["Hoa hồng", `${refCode.commission_percentage}%`, "text-brand"],
                ["Thời hạn nhận", `${refCode.period} ${t("ngày")}`, ""],
                ["Người được giới thiệu", counts.referred ?? "—", ""],
              ] as [string, string | number, string][]
            ).map(([label, value, color]) => (
              <div key={label} className="flex flex-col gap-0.5 rounded-[10px] bg-surface-2 px-3 py-2.5">
                <span className="text-xs text-muted">{t(label)}</span>
                <b className={clsx("text-lg tabular-nums", color)}>{value}</b>
              </div>
            ))}
          </div>
        </section>

        {/* Ví hoa hồng */}
        <section className="flex flex-col gap-4 rounded-[14px] border border-line bg-surface p-5">
          <div className="flex items-center gap-3">
            <span className="grid h-11 w-11 flex-none place-items-center rounded-xl bg-warn-soft text-warn">
              <IcWallet className="h-5 w-5" />
            </span>
            <div>
              <span className="block text-[12.5px] font-semibold text-muted">{t("Số dư hoa hồng")}</span>
              <b className="text-[26px] font-bold tabular-nums">{formatMoneyVN(refCode.commission_balance)}đ</b>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-[10px] bg-surface-2 px-3 py-2.5 text-[13px]">
            <span className="text-muted">{t("Tổng hoa hồng đã nhận")}</span>
            <b className="tabular-nums">{formatMoneyVN(refCode.total_commission_received)}đ</b>
          </div>
          <div className="mt-auto flex flex-col gap-1.5">
            <button
              type="button"
              className={clsx(btn, btnPrimary, "!h-11 w-full")}
              disabled={!canWithdraw}
              onClick={() => handleWithdraw(refCode)}
            >
              <IcBanknote className="h-[18px] w-[18px]" />
              {t("Rút tiền")}
            </button>
            <p className="m-0 text-center text-xs text-faint">
              {!refCode.isActive
                ? t("Mã giới thiệu đang tạm dừng, chưa rút được tiền.")
                : refCode.commission_balance < MIN_WITHDRAW
                ? `${t("Rút tối thiểu")} ${formatMoneyVN(MIN_WITHDRAW)}đ. ${t("Còn thiếu")} ${formatMoneyVN(
                    MIN_WITHDRAW - refCode.commission_balance
                  )}đ.`
                : t("Tiền được chuyển về tài khoản ngân hàng sau khi PCRender duyệt yêu cầu.")}
            </p>
          </div>
        </section>
      </div>

      {/* Lịch sử: các tab được giữ nguyên (chỉ ẩn) nên chuyển qua lại không tải lại và không nháy */}
      <section className="overflow-hidden rounded-[14px] border border-line bg-surface">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3 max-[760px]:px-3.5">
          <h2 className="flex items-center gap-2 text-[17px] font-bold">
            <IcUserPlus className="h-5 w-5 text-brand" />
            {t("Hoạt động giới thiệu")}
          </h2>
          <div role="tablist" className="rtabs inline-flex flex-wrap gap-0.5 rounded-[10px] border border-line bg-surface-2 p-[3px]">
            {tabs.map(([key, label, Icon]) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={tab === key}
                onClick={() => setTab(key)}
                className={clsx(
                  "inline-flex items-center gap-1.5 rounded-[7px] px-3 py-[5px] text-[13px] font-semibold",
                  tab === key ? "bg-surface text-fg shadow-card" : "text-muted"
                )}
              >
                <Icon className="h-4 w-4" />
                {t(label)}
                {counts[key] !== undefined && <em className="text-[11.5px] not-italic text-faint">{counts[key]}</em>}
              </button>
            ))}
          </div>
        </div>

        <div className={tab === "commission" ? "block" : "hidden"}>
          <CommissionList onCount={setCount("commission")} />
        </div>
        <div className={tab === "referred" ? "block" : "hidden"}>
          <ReferredList period={refCode.period} onCount={setCount("referred")} />
        </div>
        <div className={tab === "withdraw" ? "block" : "hidden"}>
          <WithdrawList reload={withdrawReload} onCount={setCount("withdraw")} />
        </div>
      </section>
    </div>
  );
}

export default RefCodePage;
